import React, { useEffect, useState, createContext } from 'react';
import PropTypes from 'prop-types';
import { useCookies } from 'react-cookie';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import { useVertical } from 'lib/Hooks/useVertical';

/**
 * @typedef {object} MockSubscriptionContextType
 * @property {boolean} isSubscriptionUserLoggedIn - indicating if logged in as subscribed user
 * @property {Function} setIsSubscriptionUserLoggedIn - sets the subscribed user login state
 * @returns {React.Context<MockSubscriptionContextType>}
 */
export const MockSubscriptionContext = createContext({
  // eslint-disable-next-line jsdoc/require-jsdoc
  isSubscriptionUserLoggedIn: false, setIsSubscriptionAUserLoggedIn: () => {},
});

export const mockSessionToken = '6c59ca9437535f1b923463a507b2fb8d';

/**
 * @param {React.ReactNode} children - the children to render
 * @returns {React.ReactElement}
 */
export const MockSubscriptionProvider = ({ children }) => {
  const { 'toggle-mock-subscription-cta-button': shouldAllowUserToBeLoggedIn } = useFeatureFlagContext();
  const { vertical } = useVertical();
  const [cookies, setCookie, removeCookie] = useCookies(['mock-session-token']);
  const mockSessionTokenCookie = cookies['mock-session-token'];
  const isMockSessionTokenPresent = mockSessionTokenCookie === mockSessionToken;

  const isUserLoggedInAndFeatureFlagEnabled = isMockSessionTokenPresent && shouldAllowUserToBeLoggedIn && vertical === 'news';

  const [isSubscriptionUserLoggedIn, setIsSubscriptionUserLoggedIn] = useState(isUserLoggedInAndFeatureFlagEnabled);

  useEffect(() => {
    setIsSubscriptionUserLoggedIn(isUserLoggedInAndFeatureFlagEnabled);
  }, [mockSessionTokenCookie]);

  /**
   * Sets or removes a cookie, represeting a valid session token for a subscribed user
   * Also notifies the user of their logged in state
   */
  const handleLoginLogoutButtonClick = () => {
    if (isSubscriptionUserLoggedIn) {
      removeCookie('mock-session-token');
      alert('You are logged out from your Subscription User session!');
      return;
    }

    setCookie('mock-session-token', mockSessionToken, { expires: new Date(new Date().getTime() + 10 * 60 * 1000) });
    alert('You are logged in as a Subscription User!');
  };

  return (
    <MockSubscriptionContext.Provider value={{ isSubscriptionUserLoggedIn, handleLoginLogoutButtonClick }}>
      { children }
    </MockSubscriptionContext.Provider>
  );
};

MockSubscriptionProvider.propTypes = {
  children: PropTypes.isRequired,
};
